import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Send, EyeOff, Users, ChevronDown, ChevronUp, ShieldCheck, Lock, Eye, Undo2 } from "lucide-react";

const inr = (n) => `₹${Math.round(Number(n || 0)).toLocaleString("en-IN")}`;
const fmt = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

/**
 * BuyerReleaseControls — gates the buyer-facing bureaucratic Workout PDF
 * behind an explicit admin release. Renders three affordances:
 *
 *   · Preview buyer PDF (opens the DRAFT/RELEASED version inline)
 *   · Release to Buyer  (enables buyer download + emails the buyer)
 *   · Revoke release    (once released; hides the buyer download again)
 *
 * The state pill on the left shows the current release status.
 */
function BuyerReleaseControls({ claimId, api, onChange }) {
  const [state, setState] = useState({ released: false, released_at: null, released_by: null });
  const [busy, setBusy] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/claims/${claimId}/settlement/release-status`);
      setState(r.data || { released: false });
    } catch { /* silent */ }
  }, [api, claimId]);
  useEffect(() => { load(); }, [load]);

  const release = async () => {
    const note = window.prompt("Optional note to accompany the release (buyer will see this):") || "";
    if (!window.confirm("Release the Official Financial Workout to the buyer? An email will be dispatched.")) return;
    setBusy(true);
    try {
      await api.post(`/admin/claims/${claimId}/settlement/release`, { note });
      toast.success("Financial Workout released to buyer · email dispatched");
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Release failed");
    } finally { setBusy(false); }
  };

  const revoke = async () => {
    const note = window.prompt("Reason for revoking the release (optional):") || "";
    if (!window.confirm("Revoke buyer access to the Financial Workout PDF?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/claims/${claimId}/settlement/revoke-release`, { note });
      toast.success("Buyer access revoked");
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    } finally { setBusy(false); }
  };

  const preview = () => {
    window.open(`${backend}/api/admin/claims/${claimId}/settlement-workout-buyer`, "_blank");
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`sd-release-${claimId}`}>
      {state.released ? (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-[#2FB999]/40 bg-[#2FB999]/[0.08] text-[#2FB999] px-3 py-1 font-grotesk text-[9px] uppercase tracking-[0.22em]"
              title={`Released ${fmt(state.released_at)} by ${state.released_by}`}
              data-testid={`sd-release-status-${claimId}`}>
          <ShieldCheck size={11} /> Released to buyer
        </span>
      ) : (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-[#D8B23E]/40 bg-[#D8B23E]/[0.06] text-[#D8B23E] px-3 py-1 font-grotesk text-[9px] uppercase tracking-[0.22em]"
              data-testid={`sd-release-status-${claimId}`}>
          <Lock size={11} /> Internal draft · buyer blocked
        </span>
      )}
      <button type="button" onClick={preview} data-testid={`sd-preview-buyer-${claimId}`}
              className="rounded-full border border-white/15 hover:border-white/35 text-[#8A8A82] hover:text-[#F5F5F0] px-4 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.2em]">
        <Eye size={12} className="inline mr-1 -mt-0.5" />Preview buyer PDF
      </button>
      {state.released ? (
        <button type="button" onClick={revoke} disabled={busy}
                data-testid={`sd-revoke-${claimId}`}
                className="rounded-full border border-[#C0392B]/50 text-[#C0392B] hover:bg-[#C0392B]/10 px-4 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.2em] disabled:opacity-40">
          <Undo2 size={12} className="inline mr-1 -mt-0.5" />Revoke release
        </button>
      ) : (
        <button type="button" onClick={release} disabled={busy}
                data-testid={`sd-release-btn-${claimId}`}
                className="rounded-full bg-[#8B1E1E] hover:bg-[#A82424] text-white px-5 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.22em] disabled:opacity-40 shadow-lg shadow-[#8B1E1E]/30">
          <ShieldCheck size={12} className="inline mr-1 -mt-0.5" />Release PDF to Buyer
        </button>
      )}
    </div>
  );
}

/**
 * Admin RFQ Broadcast Desk.
 *
 * Lists open buyer RFQs (from /quote-requests) and lets admin curate + broadcast
 * to matched suppliers with buyer identity masked.  After suppliers submit
 * quotes, admin reviews them here and forwards curated ones back to the buyer.
 */
export function AdminRfqBroadcast({ api }) {
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openRfq, setOpenRfq] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/quote-requests");
      setRfqs(r.data || []);
    } catch { toast.error("Could not load RFQs"); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="text-[#F5F5F0]">
      <div className="mb-8">
        <div className="overline text-[#B87333] mb-1">RFQ Broadcast Desk</div>
        <h2 className="font-serif text-3xl">Anonymised Supplier Curation</h2>
        <p className="font-body text-sm text-[#8A8A82] mt-2 max-w-2xl">
          Every buyer requirement lands here. Curate the right suppliers, broadcast anonymised copies, review incoming quotes, and forward winners back to the buyer.
        </p>
      </div>

      {loading ? <div className="p-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#B87333]" /></div> :
        rfqs.length === 0 ? <div className="p-16 text-center text-[#8A8A82] font-body text-sm border border-white/10 rounded-2xl">No RFQs yet.</div> :
          <div className="space-y-3">
            {rfqs.map((r) => <RfqRow key={r.id} rfq={r} api={api} open={openRfq === r.id} setOpen={() => setOpenRfq(openRfq === r.id ? null : r.id)} refresh={load} />)}
          </div>
      }
    </div>
  );
}

function RfqRow({ rfq, api, open, setOpen, refresh }) {
  const [detail, setDetail] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    setLoading(true);
    Promise.all([
      api.get(`/admin/rfq-broadcast/candidates/${rfq.id}`),
      api.get(`/admin/broadcast-quotes/${rfq.id}`),
    ])
      .then(([c, q]) => { if (mounted) { setDetail(c.data); setQuotes(q.data || []); } })
      .catch(() => toast.error("Could not load RFQ detail"))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [open, api, rfq.id]);

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const broadcast = async () => {
    if (selectedIds.length === 0) return toast.error("Select at least one supplier");
    setBusy(true);
    try {
      const r = await api.post(`/admin/rfq-broadcast/${rfq.id}`, { seller_ids: selectedIds });
      toast.success(`Broadcast to ${r.data.sent} supplier(s)`);
      setSelected({}); refresh();
      const c = await api.get(`/admin/rfq-broadcast/candidates/${rfq.id}`);
      setDetail(c.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Broadcast failed"); }
    finally { setBusy(false); }
  };

  const forward = async (bqid) => {
    try {
      await api.post(`/admin/broadcast-quotes/${bqid}/forward`, { admin_comment: "" });
      toast.success("Quote forwarded to buyer");
      const q = await api.get(`/admin/broadcast-quotes/${rfq.id}`);
      setQuotes(q.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Forward failed"); }
  };

  const statusTone = {
    open: "text-[#D8B23E] border-[#D8B23E]/40",
    broadcast: "text-[#B87333] border-[#B87333]/40",
    matched: "text-[#2FB999] border-[#2FB999]/40",
  }[rfq.broadcast_status || rfq.status] || "text-[#8A8A82] border-white/20";

  return (
    <div className="border border-white/10 rounded-2xl bg-white/[0.02]">
      <button data-testid={`admin-rfq-${rfq.id}`} onClick={setOpen} className="w-full flex items-center justify-between p-5 hover:bg-white/[0.02]">
        <div className="flex items-center gap-4 text-left">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] font-grotesk font-mono">{rfq.ref}</span>
          <span className="font-serif text-lg">{rfq.material} {rfq.grade ? `· ${rfq.grade}` : ""}</span>
          <span className="text-xs text-[#C9C9C2]">{rfq.email}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-[#8A8A82]">{fmt(rfq.created_at)}</span>
          <span className={`inline-block rounded-full border px-3 py-0.5 text-[10px] uppercase tracking-[0.15em] font-grotesk ${statusTone}`}>{rfq.broadcast_status || rfq.status}</span>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-white/10 p-5 space-y-6">
          {loading ? <Loader2 className="w-5 h-5 animate-spin text-[#B87333]" /> : (
            <>
              {/* Buyer preview (unmasked to admin) */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-[#C9C9C2]">
                <Info label="Buyer" value={rfq.name || rfq.email} />
                <Info label="Company" value={rfq.company || "—"} />
                <Info label="Phone" value={rfq.phone || "—"} />
                <Info label="Weight" value={`${rfq.weight_tons || "—"} MT`} />
                <Info label="Delivery" value={rfq.delivery_state || "—"} />
                <Info label="Target" value={rfq.target_delivery || "—"} />
              </div>
              {rfq.notes ? <div className="text-xs text-[#8A8A82] italic border-l-2 border-[#B87333]/40 pl-3">{rfq.notes}</div> : null}

              {/* Masked preview shown to sellers */}
              <div className="border border-[#B87333]/30 bg-[#B87333]/5 rounded-lg p-4">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#B87333] font-grotesk mb-2"><EyeOff className="w-3 h-3" /> Preview shown to suppliers</div>
                <div className="text-xs text-[#C9C9C2]">Buyer: <strong>{detail?.masked_preview?.buyer_alias}</strong> · {detail?.masked_preview?.material} · {detail?.masked_preview?.weight_tons} {detail?.masked_preview?.unit} · {detail?.masked_preview?.delivery_state}</div>
              </div>

              {/* Supplier picker */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-xs text-[#F5F5F0] font-grotesk uppercase tracking-[0.15em]"><Users className="w-4 h-4" /> Candidate Suppliers ({detail?.suppliers?.length || 0})</div>
                  <button data-testid={`bcast-send-${rfq.id}`} disabled={busy || selectedIds.length === 0} onClick={broadcast}
                    className="rounded-full bg-[#B87333] text-black px-5 py-2 text-xs uppercase tracking-[0.2em] font-grotesk hover:brightness-110 disabled:opacity-40 flex items-center gap-2">
                    <Send className="w-4 h-4" /> Broadcast ({selectedIds.length})
                  </button>
                </div>
                <div className="max-h-80 overflow-y-auto border border-white/10 rounded-lg divide-y divide-white/5">
                  {detail?.suppliers?.map((s) => (
                    <label key={s.id} data-testid={`bcast-cand-${s.id}`}
                      className={`flex items-center gap-3 px-4 py-3 hover:bg-white/[0.03] cursor-pointer ${s.matched ? "" : "opacity-70"}`}>
                      <input type="checkbox" checked={!!selected[s.id]} onChange={() => toggle(s.id)} disabled={s.already_sent}
                        className="accent-[#B87333]" />
                      <div className="flex-1 text-xs">
                        <div className="text-[#F5F5F0]">{s.business_name} {s.matched ? <span className="ml-2 text-[#2FB999] text-[10px]">MATCH</span> : ""}{s.already_sent ? <span className="ml-2 text-[#8A8A82] text-[10px]">SENT</span> : ""}</div>
                        <div className="text-[#8A8A82]">{s.email} · {s.state || "—"} · {(s.material_tags || []).join(", ") || (s.materials_supplied || "").slice(0, 40)}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Incoming quotes */}
              <div>
                <div className="text-xs text-[#F5F5F0] font-grotesk uppercase tracking-[0.15em] mb-3">Supplier Quotes ({quotes.length})</div>
                {quotes.length === 0 ? <div className="text-xs text-[#8A8A82] italic">No quotes received yet.</div> :
                  <div className="space-y-2">
                    {quotes.map((q) => (
                      <div key={q.id} data-testid={`bcast-quote-${q.id}`} className="flex items-center justify-between border border-white/10 rounded-lg p-3">
                        <div className="text-xs">
                          <div className="text-[#F5F5F0]">{q.supplier_name} <span className="text-[#8A8A82]">· {q.supplier_email}</span></div>
                          <div className="text-[#C9C9C2] mt-1">₹{q.price_per_unit?.toLocaleString?.("en-IN")} / {q.unit} · Lead {q.lead_time || "—"} · Terms {q.payment_terms || "—"}</div>
                          {q.notes ? <div className="text-[#8A8A82] italic mt-1">{q.notes}</div> : null}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`inline-block rounded-full border px-3 py-0.5 text-[10px] uppercase tracking-[0.15em] font-grotesk ${q.status === "accepted" ? "text-[#2FB999] border-[#2FB999]/40" : q.status === "forwarded_to_buyer" ? "text-[#B87333] border-[#B87333]/40" : "text-[#D8B23E] border-[#D8B23E]/40"}`}>{q.status.replace(/_/g, " ")}</span>
                          {q.status === "pending_admin_review" && (
                            <button data-testid={`bcast-fwd-${q.id}`} onClick={() => forward(q.id)} className="rounded-full bg-[#2FB999] text-black px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] font-grotesk hover:brightness-110">Forward</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                }
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
function Info({ label, value }) {
  return <div><div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] font-grotesk mb-0.5">{label}</div><div>{value}</div></div>;
}

/**
 * Admin — Buyer Credit Limits management.
 */
export function AdminCreditLimits({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buyers, setBuyers] = useState([]);
  const [grantOpen, setGrantOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, u] = await Promise.all([
        api.get("/admin/credit-limits"),
        api.get("/admin/users?role=buyer").catch(() => ({ data: { users: [] } })),
      ]);
      setRows(r.data || []);
      setBuyers(u.data.users || u.data || []);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const grant = async (uid, limit, note) => {
    try {
      await api.post(`/admin/credit-limits/${uid}`, { limit: Number(limit), note });
      toast.success("Credit limit updated"); setGrantOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const repay = async (uid) => {
    const amount = window.prompt("Repayment amount (INR)?");
    if (!amount) return;
    const ref = window.prompt("Reference (bank txn ID etc.) — optional") || "";
    try {
      await api.post(`/admin/credit-repayments/${uid}`, { amount: Number(amount), ref });
      toast.success("Repayment recorded"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="text-[#F5F5F0]">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline text-[#B87333] mb-1">Credit Desk</div>
          <h2 className="font-serif text-3xl">Buyer Credit Limits</h2>
          <p className="font-body text-sm text-[#8A8A82] mt-2">Grant, adjust or revoke instant-purchase credit for trusted buyers. All movements are audited.</p>
        </div>
        <button data-testid="credit-new" onClick={() => setGrantOpen(true)} className="rounded-full bg-[#B87333] text-black px-5 py-2 text-xs uppercase tracking-[0.2em] font-grotesk hover:brightness-110">+ Grant / Update Limit</button>
      </div>

      {loading ? <div className="p-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#B87333]" /></div> :
        <div className="border border-white/10 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-[1fr_140px_140px_140px_120px] gap-3 px-6 py-4 border-b border-white/10 bg-white/[0.02] text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] font-grotesk">
            <div>Buyer</div><div className="text-right">Limit</div><div className="text-right">Outstanding</div><div className="text-right">Available</div><div>Actions</div>
          </div>
          {rows.length === 0 ? <div className="p-12 text-center text-[#8A8A82] text-sm">No credit lines active.</div> :
            rows.map((r) => (
              <div key={r.user_id} data-testid={`credit-row-${r.user_id}`} className="grid grid-cols-1 md:grid-cols-[1fr_140px_140px_140px_120px] gap-3 px-6 py-4 border-b border-white/5 hover:bg-white/[0.02]">
                <div className="text-xs text-[#C9C9C2]">
                  <div className="text-[#F5F5F0]">{r.buyer_name || "—"}</div>
                  <div className="text-[10px] text-[#8A8A82]">{r.buyer_email} · {r.vendor_code || ""}</div>
                </div>
                <div className="text-xs text-right">{inr(r.limit)}</div>
                <div className="text-xs text-right text-[#D8B23E]">{inr(r.outstanding)}</div>
                <div className="text-xs text-right text-[#2FB999]">{inr(r.available)}</div>
                <div className="flex gap-2">
                  <button data-testid={`credit-repay-${r.user_id}`} onClick={() => repay(r.user_id)} disabled={!r.outstanding}
                    className="text-[10px] text-[#2FB999] hover:brightness-125 disabled:opacity-30">Repay</button>
                  <button data-testid={`credit-edit-${r.user_id}`} onClick={() => setGrantOpen({ uid: r.user_id, current: r.limit, email: r.buyer_email })}
                    className="text-[10px] text-[#B87333] hover:brightness-125">Edit</button>
                </div>
              </div>
            ))
          }
        </div>
      }
      {grantOpen ? <GrantModal onClose={() => setGrantOpen(false)} onSave={grant} initial={grantOpen} buyers={buyers} /> : null}
    </div>
  );
}
function GrantModal({ onClose, onSave, initial, buyers }) {
  const isEdit = initial && typeof initial === "object";
  const [uid, setUid] = useState(isEdit ? initial.uid : "");
  const [limit, setLimit] = useState(isEdit ? initial.current : "");
  const [note, setNote] = useState("");
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center px-4" onClick={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); if (!uid || !limit) return; onSave(uid, limit, note); }}
        onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[#0D0D0D] border border-[#B87333]/40 rounded-2xl p-8">
        <div className="font-serif text-2xl mb-1">{isEdit ? "Edit Credit Limit" : "Grant Credit Limit"}</div>
        <div className="text-xs text-[#8A8A82] mb-6">{isEdit ? initial.email : "Pick a verified buyer and set a limit."}</div>
        {!isEdit && (
          <label className="block mb-4"><div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] font-grotesk mb-1.5">Buyer *</div>
            <select data-testid="credit-buyer-select" value={uid} onChange={(e) => setUid(e.target.value)} className="inp">
              <option value="">— Select buyer —</option>
              {buyers.map((b) => <option key={b.id || b._id} value={b.id || b._id}>{b.email} · {(b.profile || {}).business_name || b.name || ""}</option>)}
            </select></label>
        )}
        <label className="block mb-4"><div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] font-grotesk mb-1.5">Limit (INR) *</div>
          <input data-testid="credit-limit-input" type="number" value={limit} onChange={(e) => setLimit(e.target.value)} className="inp" /></label>
        <label className="block mb-6"><div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] font-grotesk mb-1.5">Note (internal)</div>
          <input data-testid="credit-note-input" value={note} onChange={(e) => setNote(e.target.value)} className="inp" /></label>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="rounded-full border border-white/20 px-5 py-2 text-xs uppercase tracking-[0.2em] font-grotesk">Cancel</button>
          <button data-testid="credit-save" type="submit" className="rounded-full bg-[#B87333] text-black px-6 py-2 text-xs uppercase tracking-[0.2em] font-grotesk hover:brightness-110">Save</button>
        </div>
        <style>{`.inp{width:100%;background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.15);border-radius:8px;padding:.6rem .8rem;font-size:12px;color:#F5F5F0;outline:none}.inp:focus{border-color:#B87333}`}</style>
      </form>
    </div>
  );
}

/**
 * Admin — Supplier Commission Rates & Payout Ledger.
 */
export function AdminSettlementDesk({ api }) {
  const [data, setData] = useState({ default_pct: 7.5, sellers: [] });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [defaultPct, setDefaultPct] = useState("");
  const [tab, setTab] = useState("claims");
  const [claims, setClaims] = useState([]);
  const [claimStatus, setClaimStatus] = useState("eligible");
  const [openId, setOpenId] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());

  const toggleSel = (id) => setSelectedIds(s => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const selectAll = () => setSelectedIds(new Set(claims.map(c => c.id)));
  const clearSel = () => setSelectedIds(new Set());

  const bulkTriage = async (action) => {
    if (selectedIds.size === 0) { toast.error("Select at least one claim"); return; }
    let reason = "";
    if (["accept_with_reason","reject_with_reason","re_register"].includes(action)) {
      reason = window.prompt(`Bulk ${action.replace(/_/g," ")} · enter reason (applies to all ${selectedIds.size})`) || "";
      if (!reason.trim()) { toast.error("Reason required"); return; }
    } else {
      if (!window.confirm(`Bulk ${action} across ${selectedIds.size} claims — emails will be sent. Proceed?`)) return;
    }
    try {
      const r = await api.post("/admin/claims/settlement/bulk-triage",
        { claim_ids: Array.from(selectedIds), action, reason });
      toast.success(`Processed ${r.data.processed} · OK ${r.data.ok} · errors ${r.data.errors_count}`);
      clearSel(); loadClaims();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk failed"); }
  };

  const loadClaims = useCallback(async () => {
    try {
      const r = await api.get(`/admin/claims/settlement-queue?status=${claimStatus}`);
      setClaims(r.data?.items || []);
    } catch { toast.error("Could not load settlement queue"); }
  }, [api, claimStatus]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, a] = await Promise.all([
        api.get("/admin/settlement/commission-rates"),
        api.get("/admin/settlement/all"),
      ]);
      setData(c.data); setDefaultPct(c.data.default_pct);
      setRows(a.data || []);
    } catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); loadClaims(); }, [load, loadClaims]);
  useEffect(() => { loadClaims(); }, [loadClaims]);

  const updateSeller = async (sid, pct) => {
    try {
      await api.patch(`/admin/settlement/commission-rate/${sid}`, { commission_pct: Number(pct) });
      toast.success("Rate updated"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveDefault = async () => {
    try {
      await api.post("/admin/settlement/default-commission", { default_commission_pct: Number(defaultPct) });
      toast.success("Default updated"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const pending = rows.filter((r) => r.payout_status !== "paid" && r.payment_status === "paid");
  const markPaid = async (sid, orderIds) => {
    const ref = window.prompt("Bank reference / UTR (optional)") || "";
    try {
      await api.post(`/admin/settlement/${sid}/mark-paid`, { order_ids: orderIds, payout_ref: ref });
      toast.success(`Marked ${orderIds.length} payout(s) paid`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const bySupplier = pending.reduce((acc, r) => { (acc[r.supplier_email] ||= { name: r.supplier_name, rows: [] }).rows.push(r); return acc; }, {});
  const supplierIdByEmail = Object.fromEntries((data.sellers || []).map((s) => [s.email, s.id]));

  return (
    <div className="text-[#F5F5F0]">
      <div className="mb-8">
        <div className="overline text-[#B87333] mb-1">Settlement Desk</div>
        <h2 className="font-serif text-3xl">Supplier Payouts & Commission</h2>
        <p className="font-body text-sm text-[#8A8A82] mt-2">Configure per-supplier commissions and clear pending payouts once bank transfers are done.</p>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {[["claims", "Claim Settlements"], ["rates", "Commission Rates"], ["pending", `Pending Payouts (${pending.length})`]].map(([k, l]) => (
          <button key={k} data-testid={`sd-tab-${k}`} onClick={() => setTab(k)}
            className={`rounded-full px-5 py-2 text-xs uppercase tracking-[0.2em] font-grotesk ${tab === k ? "bg-[#F5F5F0] text-black" : "border border-white/20 text-[#C9C9C2] hover:border-[#B87333]"}`}>{l}</button>
        ))}
      </div>

      {tab === "claims" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex gap-1 rounded-full border border-white/10 p-1 bg-black/40 w-fit">
              {[["eligible","Eligible"],["settled","Settled"],["all","All"]].map(([k,l]) => (
                <button key={k} data-testid={`sd-claim-status-${k}`}
                        onClick={() => setClaimStatus(k)}
                        className={`px-4 py-1.5 rounded-full font-grotesk text-[10px] uppercase tracking-[0.18em] ${claimStatus === k ? "bg-[#D8B23E] text-black" : "text-[#B8B8B0] hover:text-[#F5F5F0]"}`}>{l}</button>
              ))}
            </div>
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2 flex-wrap" data-testid="sd-bulk-bar">
                <span className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#D8B23E]">{selectedIds.size} selected</span>
                {[
                  { k: "accept", l: "Bulk accept", c: "#2FB999" },
                  { k: "accept_with_reason", l: "Accept + reason", c: "#2FB999" },
                  { k: "hold", l: "Hold", c: "#D8B23E" },
                  { k: "reject", l: "Reject", c: "#C0392B" },
                  { k: "reject_with_reason", l: "Reject + reasons", c: "#C0392B" },
                  { k: "re_register", l: "Re-register", c: "#B87333" },
                ].map(b => (
                  <button key={b.k} data-testid={`sd-bulk-${b.k}`} onClick={() => bulkTriage(b.k)}
                          className="rounded-full px-3 py-1 font-grotesk text-[10px] uppercase tracking-[0.18em]"
                          style={{ borderWidth: 1, borderStyle: "solid", borderColor: `${b.c}66`, color: b.c }}>
                    {b.l}
                  </button>
                ))}
                <button onClick={clearSel} data-testid="sd-bulk-clear"
                        className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] hover:text-[#F5F5F0]">Clear</button>
              </div>
            )}
            {claims.length > 0 && selectedIds.size === 0 && (
              <button onClick={selectAll} data-testid="sd-bulk-select-all"
                      className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] hover:text-[#D8B23E]">
                Select all {claims.length}
              </button>
            )}
          </div>
          {claims.length === 0 && (
            <div className="border border-dashed border-white/12 rounded-sm p-8 text-center font-body text-sm text-[#8A8A82]">
              No claims in the {claimStatus} bucket.
            </div>
          )}
          {claims.map((c) => {
            const s = c.settlement || {};
            const isOpen = openId === c.id;
            const finalized = !!c.settlement_finalized_at;
            const ov = overrides[c.id] || {};
            return (
              <div key={c.id} data-testid={`sd-claim-row-${c.id}`}
                   className="border border-white/10 rounded-sm bg-[#0a0a0a]">
                <div className="w-full p-4 flex items-center gap-4">
                  <input type="checkbox" data-testid={`sd-select-${c.id}`}
                         checked={selectedIds.has(c.id)}
                         onChange={() => toggleSel(c.id)}
                         className="accent-[#D8B23E] shrink-0"/>
                  <button type="button" onClick={() => setOpenId(isOpen ? null : c.id)}
                          className="flex-1 flex items-center gap-4 hover:bg-white/[0.02] text-left">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-serif text-base text-[#F5F5F0]">{c.order_ref}</span>
                      <span className="font-grotesk text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm border border-[#B87333]/40 text-[#B87333]">{c.kind}</span>
                      <span className="font-grotesk text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm border border-white/15 text-[#8A8A82]">{c.status}</span>
                      {finalized && <span className="font-grotesk text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm border border-[#2FB999]/40 text-[#2FB999]">Finalized</span>}
                      {c.voucher_no && <span className="font-grotesk text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm border border-[#D8B23E]/40 text-[#D8B23E]">Voucher · {c.voucher_no.split('/').pop()}</span>}
                    </div>
                    <div className="font-body text-[11px] text-[#8A8A82] mt-1">{c.buyer_email}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-serif text-lg tabular-nums text-[#F5F5F0]">₹{(s.net_payable_inr || 0).toLocaleString("en-IN")}</div>
                    <div className="font-grotesk text-[9px] uppercase tracking-[0.18em] text-[#8A8A82]">Net · gross ₹{(s.gross_claim_inr || 0).toLocaleString("en-IN")} · {s.deduction_rate_pct}% cut</div>
                  </div>
                  </button>
                </div>
                {isOpen && (
                  <div className="border-t border-white/[0.06] p-4 space-y-3">
                    <table className="w-full text-[11px] font-body">
                      <thead className="text-[9px] font-grotesk uppercase tracking-[0.18em] text-[#8A8A82] text-left">
                        <tr><th className="pb-1.5 w-6">#</th><th>Line item</th><th>Notes</th><th className="text-right">₹</th></tr>
                      </thead>
                      <tbody>
                        {(s.line_items || []).map((ln, i) => (
                          <tr key={i} className="border-t border-white/[0.04]">
                            <td className="py-1.5 text-[#8A8A82]">{i+1}</td>
                            <td className="text-[#F5F5F0]">{ln.label}</td>
                            <td className="text-[#8A8A82]">{ln.note}</td>
                            <td className="text-right tabular-nums" style={{ color: ln.amount < 0 ? "#C0392B" : "#2FB999" }}>{ln.amount.toLocaleString("en-IN")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-white/[0.06]">
                      <span className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#D8B23E] mr-1">Triage</span>
                      {[
                        { k: "accept",              l: "Accept",              c: "#2FB999", ask: false },
                        { k: "accept_with_reason",  l: "Accept + reason",     c: "#2FB999", ask: true  },
                        { k: "hold",                l: "Hold",                c: "#D8B23E", ask: false },
                        { k: "reject",              l: "Reject",              c: "#C0392B", ask: false },
                        { k: "reject_with_reason",  l: "Reject + reasons",    c: "#C0392B", ask: true  },
                        { k: "re_register",         l: "Re-register",         c: "#B87333", ask: true  },
                      ].map(b => (
                        <button key={b.k} type="button" data-testid={`sd-triage-${b.k}-${c.id}`}
                                onClick={async () => {
                                  let reason = "";
                                  if (b.ask) {
                                    reason = window.prompt(`${b.l} · enter reason for the buyer`) || "";
                                    if (!reason.trim()) { toast.error("Reason required."); return; }
                                  }
                                  try {
                                    await api.post(`/admin/claims/${c.id}/settlement/triage`,
                                      { action: b.k, reason });
                                    toast.success(`${b.l} · email dispatched to ${c.buyer_email}`);
                                    loadClaims();
                                  } catch (e) { toast.error(e?.response?.data?.detail || "Triage failed"); }
                                }}
                                className="rounded-full px-3 py-1 font-grotesk text-[10px] uppercase tracking-[0.18em]"
                                style={{ borderWidth: 1, borderStyle: "solid", borderColor: `${b.c}66`, color: b.c }}>
                          {b.l}
                        </button>
                      ))}
                      {c.settlement_triage && (
                        <span className="ml-auto font-grotesk text-[9px] uppercase tracking-[0.18em] text-[#8A8A82]">
                          Last · {c.settlement_triage.action?.replace(/_/g," ")} by {c.settlement_triage.decided_by} · {new Date(c.settlement_triage.decided_at).toLocaleString("en-IN")}
                        </span>
                      )}
                    </div>

                    {(c.settlement_triage_history || []).length > 0 && (
                      <details className="border-t border-white/[0.06] pt-2 mt-1">
                        <summary className="cursor-pointer font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#8A8A82] hover:text-[#D8B23E]">
                          Audit log · {(c.settlement_triage_history || []).length} decisions
                        </summary>
                        <div className="mt-2 space-y-1">
                          {(c.settlement_triage_history || []).slice().reverse().map((h, i) => (
                            <div key={i} data-testid={`sd-audit-${c.id}-${i}`}
                                 className="text-[11px] font-body border-l-2 border-white/[0.08] pl-3 py-1">
                              <span className="text-[#F5F5F0]">{h.action?.replace(/_/g," ")}</span>
                              <span className="text-[#8A8A82]"> · by {h.decided_by} · {new Date(h.decided_at).toLocaleString("en-IN")}</span>
                              {h.reason && <div className="text-[#B8B8B0] italic mt-0.5">"{h.reason}"</div>}
                              {h.voucher_no && <div className="text-[#D8B23E]">Voucher: {h.voucher_no}</div>}
                            </div>
                          ))}
                        </div>
                      </details>
                    )}

                    <div className="flex items-center gap-3 flex-wrap pt-2 border-t border-white/[0.06]">
                      <div className="flex items-center gap-2">
                        <span className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#8A8A82]">Override net ₹</span>
                        <input type="number" data-testid={`sd-override-input-${c.id}`}
                               value={ov.net || ""}
                               placeholder={s.net_payable_inr}
                               onChange={(e) => setOverrides(o => ({ ...o, [c.id]: { ...ov, net: e.target.value }}))}
                               className="w-32 bg-white/5 border border-white/15 rounded-sm px-2 py-1 text-xs tabular-nums text-[#F5F5F0] focus:outline-none focus:border-[#D8B23E]"/>
                        <input type="text" data-testid={`sd-override-reason-${c.id}`}
                               value={ov.reason || ""}
                               placeholder="Reason (min 6 chars)"
                               onChange={(e) => setOverrides(o => ({ ...o, [c.id]: { ...ov, reason: e.target.value }}))}
                               className="w-56 bg-white/5 border border-white/15 rounded-sm px-2 py-1 text-xs text-[#F5F5F0] focus:outline-none focus:border-[#D8B23E]"/>
                        <button type="button" data-testid={`sd-override-save-${c.id}`}
                                disabled={finalized}
                                onClick={async () => {
                                  const v = overrides[c.id] || {};
                                  if (!v.net || !v.reason || v.reason.length < 6) { toast.error("Set net + reason"); return; }
                                  try {
                                    await api.post(`/admin/claims/${c.id}/settlement/override`,
                                      { net_payable_inr: Number(v.net), reason: v.reason });
                                    toast.success("Override recorded"); loadClaims();
                                  } catch (e) { toast.error(e?.response?.data?.detail || "Override failed"); }
                                }}
                                className="rounded-full border border-[#D8B23E]/40 text-[#D8B23E] hover:bg-[#D8B23E]/10 px-4 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.2em] disabled:opacity-40">
                          Save override
                        </button>
                      </div>
                      <button type="button" data-testid={`sd-finalize-${c.id}`}
                              disabled={finalized}
                              onClick={async () => {
                                if (!window.confirm(`Finalize settlement for ${c.order_ref}? This cannot be undone.`)) return;
                                try {
                                  await api.post(`/admin/claims/${c.id}/settlement/finalize`, {});
                                  toast.success("Settlement finalized"); loadClaims();
                                } catch (e) { toast.error(e?.response?.data?.detail || "Finalize failed"); }
                              }}
                              className="rounded-full bg-[#2FB999] text-black px-4 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.2em] disabled:opacity-40">
                        {finalized ? "Finalized" : "Finalize"}
                      </button>
                      <button type="button" data-testid={`sd-download-${c.id}`}
                              onClick={async () => {
                                try {
                                  const r = await api.get(`/admin/claims/${c.id}/settlement-worksheet`, { responseType: "blob" });
                                  const u = URL.createObjectURL(r.data); const a = document.createElement("a");
                                  a.href = u; a.download = `Vinci-Settlement-Internal-${c.id.slice(0,8).toUpperCase()}.pdf`;
                                  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
                                } catch { toast.error("Download failed"); }
                              }}
                              className="rounded-full border border-white/15 hover:border-white/35 text-[#8A8A82] hover:text-[#F5F5F0] px-4 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.2em]">
                        Internal workout PDF
                      </button>
                      <BuyerReleaseControls claimId={c.id} api={api} onChange={loadClaims} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {loading ? <div className="p-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#B87333]" /></div> : tab === "rates" ? (
        <>
          <div className="border border-white/10 rounded-2xl p-5 mb-6 flex items-center gap-4">
            <div className="text-xs text-[#8A8A82] uppercase tracking-[0.2em] font-grotesk">Default Rate</div>
            <input data-testid="default-pct-input" type="number" step="0.1" value={defaultPct} onChange={(e) => setDefaultPct(e.target.value)}
              className="w-24 bg-white/5 border border-white/15 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-[#B87333]" />
            <span className="text-xs text-[#C9C9C2]">%</span>
            <button data-testid="save-default-pct" onClick={saveDefault} className="rounded-full bg-[#B87333] text-black px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] font-grotesk">Save</button>
          </div>
          <div className="border border-white/10 rounded-2xl overflow-hidden">
            <div className="grid grid-cols-[1fr_120px_140px] gap-3 px-6 py-4 border-b border-white/10 bg-white/[0.02] text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] font-grotesk">
              <div>Supplier</div><div className="text-right">Rate %</div><div>Custom?</div>
            </div>
            {data.sellers.map((s) => (
              <div key={s.id} data-testid={`rate-row-${s.id}`} className="grid grid-cols-1 md:grid-cols-[1fr_120px_140px] gap-3 px-6 py-3 border-b border-white/5 hover:bg-white/[0.02] items-center">
                <div className="text-xs">
                  <div className="text-[#F5F5F0]">{s.business_name}</div>
                  <div className="text-[10px] text-[#8A8A82]">{s.email} · {s.vendor_code || ""}</div>
                </div>
                <div className="text-right">
                  <input data-testid={`rate-input-${s.id}`} type="number" step="0.1" defaultValue={s.commission_pct}
                    onBlur={(e) => { if (Number(e.target.value) !== s.commission_pct) updateSeller(s.id, e.target.value); }}
                    className="w-20 bg-white/5 border border-white/15 rounded-lg px-2 py-1 text-xs outline-none focus:border-[#B87333] text-right" />
                </div>
                <div className="text-xs text-[#C9C9C2]">{s.is_custom ? <span className="text-[#B87333]">Custom</span> : "Default"}</div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="space-y-4">
          {Object.keys(bySupplier).length === 0 ? <div className="p-12 text-center text-[#8A8A82] text-sm border border-white/10 rounded-2xl">All payouts cleared.</div> :
            Object.entries(bySupplier).map(([email, group]) => (
              <div key={email} data-testid={`payout-group-${email}`} className="border border-white/10 rounded-2xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm text-[#F5F5F0]">{group.name} <span className="text-[10px] text-[#8A8A82] ml-2">{email}</span></div>
                  <button data-testid={`payout-clear-${email}`} onClick={() => markPaid(supplierIdByEmail[email], group.rows.map((r) => r.order_id))}
                    className="rounded-full bg-[#2FB999] text-black px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] font-grotesk hover:brightness-110">Mark all paid</button>
                </div>
                <div className="space-y-1 text-xs">
                  {group.rows.map((r) => (
                    <div key={r.order_id} className="flex justify-between text-[#C9C9C2] py-1">
                      <div>{r.ref} · {fmt(r.date)}</div><div className="text-[#2FB999]">{inr(r.net_payable)}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
